import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material"

import { clientApi, projectApi, taskApi, timeApi } from "../../api"
import { usePermissions } from "../../permissions"
import { humanDuration, parseDuration } from "../../time/duration"
import { isoDay } from "./weeks"
import ClientSelect from "../../components/ClientSelect"
import ProjectSelect from "../../components/ProjectSelect"

/**
 * Log a stint by hand, or fix one.
 *
 * ── The duration box takes what people type ─────────────────────────────────
 *
 * "1:30", "90m", "1h30m" and "1.5" all mean the same thing, and which one
 * somebody reaches for depends on what they were doing a second earlier. A field
 * that accepts only one of them is a field people get wrong, and the wrong value
 * ends up on an invoice.
 *
 * ── Client and project narrow the task list ─────────────────────────────────
 *
 * The task box searches the server and shows twenty matches. That is fine while
 * there are thirty tasks and useless once there are three thousand, because the
 * twenty you get back are twenty of the wrong client's. Picking a client, then a
 * project, is how somebody who already knows what they worked on says so without
 * having to remember a task code — and it is passed to the server as a filter
 * rather than applied here, so the twenty that come back are twenty candidates
 * instead of twenty rows to throw away.
 */
export default function LogTimeDialog({ open, mode = "create", entry = null, defaultDate, onClose, onSaved }) {
  const isEdit = mode === "edit"

  const { isBackOffice } = usePermissions()

  const [clientId, setClientId] = useState("")
  const [clients, setClients] = useState([])
  const [projectId, setProjectId] = useState("")
  const [projects, setProjects] = useState([])
  const [task, setTask] = useState(null)
  const [taskOptions, setTaskOptions] = useState([])
  const [taskQuery, setTaskQuery] = useState("")
  const [date, setDate] = useState("")
  const [startTime, setStartTime] = useState("09:00")
  const [duration, setDuration] = useState("")
  const [description, setDescription] = useState("")
  const [billable, setBillable] = useState(true)
  const [reason, setReason] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setBusy(false)
    setReason("")
    if (isEdit && entry) {
      const started = new Date(entry.started_at)
      setDate(isoDay(started))
      setStartTime(started.toTimeString().slice(0, 5))
      setDuration(humanDuration(entry.duration_seconds))
      setDescription(entry.description || "")
      setBillable(Boolean(entry.is_billable))
      setTask(entry.task_id ? { id: entry.task_id, task_code: entry.task_code, title: entry.task_title } : null)
    } else {
      setDate(isoDay(defaultDate || new Date()))
      setStartTime("09:00")
      setDuration("")
      setDescription("")
      setBillable(true)
      setTask(null)
      setClientId("")
      setProjectId("")
    }
  }, [open, isEdit, entry, defaultDate])

  // Only worth fetching for somebody who can see more than one: a client user
  // has exactly one organization, and a select with one option is furniture.
  useEffect(() => {
    if (!open || isEdit || !isBackOffice) return
    let cancelled = false
    clientApi
      .list()
      .then((data) => {
        if (!cancelled) setClients(data.organizations || [])
      })
      .catch(() => {
        if (!cancelled) setClients([])
      })
    return () => {
      cancelled = true
    }
  }, [open, isEdit, isBackOffice])

  /*
   * Projects follow the client, and the project clears with it.
   *
   * Leaving a stale project id behind would send the server two filters that
   * cannot both be true and return nothing, which reads to the person doing it
   * as "this client has no tasks".
   */
  useEffect(() => {
    if (!open || isEdit) return undefined
    setProjectId("")
    let cancelled = false
    projectApi
      .list({ client_id: clientId || undefined, limit: 200 })
      .then((data) => {
        if (!cancelled) setProjects(data.projects || [])
      })
      .catch(() => {
        if (!cancelled) setProjects([])
      })
    return () => {
      cancelled = true
    }
  }, [open, isEdit, clientId])

  // Searched server-side rather than filtering a full list in the browser: a
  // back-office agent's task list spans every client and is not a dropdown.
  useEffect(() => {
    if (!open || isEdit) return undefined
    let cancelled = false
    const timer = setTimeout(() => {
      taskApi
        .list({
          q: taskQuery || undefined,
          client_id: clientId || undefined,
          project_id: projectId || undefined,
          limit: 20,
        })
        .then((data) => {
          if (!cancelled) setTaskOptions(data.tasks || [])
        })
        .catch(() => {
          if (!cancelled) setTaskOptions([])
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, isEdit, taskQuery, clientId, projectId])

  /* A task chosen before the filters were narrowed can end up outside them. It
   * stays selected but stops being visible, which is the one state where what
   * gets logged is not what is on screen. */
  useEffect(() => {
    if (!task) return
    const clientMismatch = clientId && String(task.client_id ?? "") !== String(clientId)
    const projectMismatch = projectId && String(task.project_id ?? "") !== String(projectId)
    if (clientMismatch || projectMismatch) setTask(null)
  }, [clientId, projectId, task])

  const seconds = useMemo(() => parseDuration(duration), [duration])

  /*
   * When it ended — the clock-out time.
   *
   * Derived from the start and the duration rather than stored beside them:
   * three fields that can each be edited and must agree are two that will
   * disagree. Typing an end time rewrites the duration instead, so somebody
   * reviewing an entry against their own notes ("11:21 to 12:23") can enter it
   * the way they wrote it down. An end at or before the start means it ran past
   * midnight, which is said underneath rather than guessed silently.
   */
  const { endTime, endDayOffset } = useMemo(() => {
    if (!startTime || !seconds) return { endTime: "", endDayOffset: 0 }
    const [h, m] = startTime.split(":").map(Number)
    const endMinutes = h * 60 + m + Math.floor(seconds / 60)
    return {
      endTime: `${String(Math.floor(endMinutes / 60) % 24).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`,
      endDayOffset: Math.floor(endMinutes / 1440),
    }
  }, [startTime, seconds])

  const setEndTime = (value) => {
    if (!value || !startTime) return
    const [sh, sm] = startTime.split(":").map(Number)
    const [eh, em] = value.split(":").map(Number)
    let minutes = eh * 60 + em - (sh * 60 + sm)
    if (minutes <= 0) minutes += 1440
    setDuration(humanDuration(minutes * 60))
  }
  const durationHelp = duration && seconds == null ? "Try 1:30, 90m, 45s or 1.5" : seconds ? humanDuration(seconds) : "1:30, 90m, 45s or 1.5"

  const submit = async (event) => {
    event.preventDefault()
    if (!seconds) {
      setError("That duration could not be read. Try 1:30, 90m, 45s or 1.5.")
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (isEdit) {
        await timeApi.updateEntry(entry.id, {
          started_at: new Date(`${date}T${startTime}`).toISOString(),
          duration_seconds: seconds,
          description: description.trim() || null,
          is_billable: billable,
          reason: reason.trim() || null,
        })
        onSaved("Entry updated.")
      } else {
        await timeApi.logEntry({
          task_id: task?.id ?? undefined,
          started_at: new Date(`${date}T${startTime}`).toISOString(),
          duration_seconds: seconds,
          description: description.trim() || null,
          is_billable: billable,
        })
        onSaved(`Logged ${humanDuration(seconds)}.`)
      }
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>{isEdit ? "Edit entry" : "Log time"}</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {isEdit && entry?.signed_off_at ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This entry has been signed off. Changing it clears the signature — a signature is a statement about what
              was there at the time.
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            {!isEdit && isBackOffice ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <ClientSelect
                  clients={clients}
                  value={clientId}
                  onChange={setClientId}
                  emptyLabel="Any client"
                  fullWidth
                  helperText="Narrows the tasks below"
                />
              </Grid>
            ) : null}

            {!isEdit ? (
              <Grid size={{ xs: 12, sm: isBackOffice ? 6 : 12 }}>
                <ProjectSelect
                  projects={projects}
                  value={projectId}
                  onChange={setProjectId}
                  emptyLabel="Any project"
                  fullWidth
                  disabled={projects.length === 0}
                  helperText={projects.length === 0 ? "No projects to filter by" : "Narrows the tasks below"}
                />
              </Grid>
            ) : null}

            {!isEdit ? (
              <Grid size={12}>
                <Autocomplete
                  options={taskOptions}
                  value={task}
                  onChange={(_e, value) => setTask(value)}
                  onInputChange={(_e, value) => setTaskQuery(value)}
                  getOptionLabel={(option) => (option?.task_code ? `${option.task_code}: ${option.title}` : option?.title || "")}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Task"
                      helperText={
                        clientId || projectId
                          ? "Showing tasks in the selection above"
                          : "Optional — time can belong to a client without a task"
                      }
                    />
                  )}
                />
              </Grid>
            ) : null}

            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Started at"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField
                label="Ended at"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                fullWidth
                disabled={!seconds}
                helperText={
                  !seconds
                    ? "From the duration"
                    : endDayOffset === 1
                      ? "The next day"
                      : endDayOffset > 1
                        ? `${endDayOffset} days later`
                        : " "
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                fullWidth
                required
                autoFocus={isEdit}
                error={Boolean(duration) && seconds == null}
                helperText={durationHelp}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label="What were you working on?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                helperText="This is what a client may see on their invoice, so write it for them."
              />
            </Grid>

            {isEdit ? (
              <Grid size={12}>
                <TextField
                  label="Why is this changing?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  fullWidth
                  helperText="Kept with the entry. Time is the evidence behind an invoice, so edits are recorded."
                />
              </Grid>
            ) : null}

            <Grid size={12}>
              <FormControlLabel
                control={<Switch checked={billable} onChange={(e) => setBillable(e.target.checked)} />}
                label="Billable"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Unticked, the hours are still tracked — they just cannot reach an invoice.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !seconds}>
            {busy ? "Saving…" : isEdit ? "Save changes" : `Log ${seconds ? humanDuration(seconds) : "time"}`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
